import { useAdminFetchAgency } from "@portal/api/agency/admin";
import { useAccountFollowers } from "@portal/api/followers";
import { AgentFollowToggle } from "@portal/components/features/followers/AgentFollowToggle";
import { Icon } from "@portal/components/primitives";
import { getStatusCardByStatus } from "@portal/components/ui/StatusCard";
import { TruncatedLabel } from "@portal/components/ui/TruncatedLabel";
import { type ColumnWidthSource, useColumnWidth } from "@portal/hooks/useColumnWidth";
import { FC } from "react";
import type { AccountIdentity } from "./AccountHeader";

export type StickyAccountPanelProps = {
	account: AccountIdentity;
	isAdmin: boolean;
	column: ColumnWidthSource;
};

type StickyPanelProps = Omit<StickyAccountPanelProps, "isAdmin">;

// 25px under the sm screen (850). Matching sm exactly lets the tools rail
// collapsing change the column width enough to hide and show the status icon.
const collapsedStatusMinWidth = 825;

const AgentStickyAccountPanel: FC<StickyPanelProps> = ({ account, column }) => {
	const followersControl = useAccountFollowers(account.id, false);
	const showStatus = useColumnWidth(
		column,
		(width) => width === undefined || width >= collapsedStatusMinWidth,
	);

	return (
		<div
			data-testid="agent-sticky-account-panel"
			className="flex items-center gap-2 whitespace-nowrap"
		>
			{showStatus && getStatusCardByStatus(account.status, "", { iconOnly: true })}
			<div data-testid="agent-sticky-followers" className="hidden xl:flex">
				<AgentFollowToggle
					isFollowing={followersControl.isFollowing}
					followers={followersControl.followers}
					onFollow={() => followersControl.createAgentFollower(undefined)}
					onUnfollow={() => followersControl.removeAgentFollower(undefined)}
					trackingId="account-agent-follow-sticky"
				/>
			</div>
		</div>
	);
};

const AdminStickyAccountPanel: FC<StickyPanelProps> = ({ account, column }) => {
	const { agency, agencyLoading } = useAdminFetchAgency(account.agencyId);
	const showAgency = useColumnWidth(column, (width) => width === undefined || width >= 1920);
	const showStatus = useColumnWidth(
		column,
		(width) => width === undefined || width >= collapsedStatusMinWidth,
	);

	if (agencyLoading) {
		return null;
	}

	return (
		<div
			data-testid="admin-sticky-account-panel"
			className="flex items-center gap-2 whitespace-nowrap"
		>
			{showAgency && (
				<div
					data-testid="agency"
					title="Broker of Record"
					className="flex items-center space-x-[8px]"
				>
					<Icon type="user-group" size="mini" color="secondary" />
					<TruncatedLabel
						label={agency?.displayName || "Unknown Agency"}
						className="max-w-[150px] text-[12px] font-[550] text-secondary"
					/>
				</div>
			)}
			{showStatus && getStatusCardByStatus(account.status, "", { iconOnly: true })}
		</div>
	);
};

export const StickyAccountPanel: FC<StickyAccountPanelProps> = ({ account, isAdmin, column }) =>
	isAdmin ? (
		<AdminStickyAccountPanel account={account} column={column} />
	) : (
		<AgentStickyAccountPanel account={account} column={column} />
	);
